import { LitElement, html } from 'lit';
import { getAuth, signInWithPopup, GoogleAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { API_ENDPOINT } from '../entry/constants';

export class AuthPrompt extends LitElement {
  createRenderRoot() {
    return this; // Use light DOM for Tailwind styling
  }

  static properties = {
    isOpen: { type: Boolean },
    email: { type: String },
    password: { type: String },
  };

  constructor() {
    super();
    this.isOpen = false;
    this.email = '';
    this.password = '';
  }

  async signInWithGoogle() {
    try {
      const auth = getAuth();
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      await this.exchangeToken(idToken);
      this.dispatchEvent(new CustomEvent('auth-success'));
      this.close();
    } catch (err) {
      console.error('Google sign-in failed:', err);
      alert(err.message);
    }
  }

  async signInOrSignUpWithEmail() {
    const auth = getAuth();

    try {
      // First attempt to sign in
      const signInResult = await signInWithEmailAndPassword(auth, this.email, this.password);
      const signInToken = await signInResult.user.getIdToken();
      await this.exchangeToken(signInToken);
      this.dispatchEvent(new CustomEvent('auth-success'));
      this.close();
    } catch (signInErr) {
      // If sign-in fails due to user not existing, attempt sign-up
      if (signInErr.code === 'auth/user-not-found' || signInErr.code == 'auth/invalid-credential') {
        try {
          const signUpResult = await createUserWithEmailAndPassword(auth, this.email, this.password);
          const signUpToken = await signUpResult.user.getIdToken();
          await this.exchangeToken(signUpToken);
          this.dispatchEvent(new CustomEvent('auth-success'));
          this.close();
        } catch (signUpErr) {
          console.error('Sign-up failed:', signUpErr);
          this.errorMessage = signUpErr.message;
        }
      } else {
        // Handle other sign-in errors (e.g., wrong password)
        console.error('Sign-in failed:', signInErr);
        this.errorMessage = signInErr.message === 'Firebase: Error (auth/wrong-password).' ? 'Incorrect password. Please try again.' : signInErr.message;
      }
    }
  }

  async exchangeToken(idToken: string) {
    await fetch(`${API_ENDPOINT}/auth/firebase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
      credentials: 'include',
    });
  }

  open() {
    this.isOpen = true;
  }

  close() {
    this.dispatchEvent(new CustomEvent('cancel'));
    this.email = '';
    this.password = '';
  }

  render() {
    if (!this.isOpen) return null;

    return html`
      <div class="fixed inset-0 z-50 flex items-center justify-center" style="background: rgba(0,0,0,0.9)">
        <div class="bg-gray-800 p-6 rounded w-96 max-w-md">
          <h2 class="text-xl font-bold mb-4">Sign In To Continue</h2>
          
          <div class="mb-4">
            <input
              type="email"
              placeholder="Email"
              class="input input-bordered w-full mb-2"
              .value=${this.email}
              @input=${(e) => (this.email = e.target.value)}
            />
            <input
              type="password"
              placeholder="Password"
              class="input input-bordered w-full mb-2"
              .value=${this.password}
              @input=${(e) => (this.password = e.target.value)}
            />
            <button
              class="btn btn-primary w-full"
              @click=${this.signInOrSignUpWithEmail}
            >
              Sign In
            </button>
          </div>

          <div class="divider">or</div>

          <button
            class="btn btn-outline btn-secondary w-full flex items-center justify-center gap-2"
            @click=${this.signInWithGoogle}
          >
            <svg class="w-6 h-6" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
              <path fill="none" d="M0 0h48v48H0z"></path>
            </svg>
            Continue with Google
          </button>

          <button
            class="btn btn-ghost mt-4 w-full"
            @click=${this.close}
          >
            Cancel
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('auth-prompt', AuthPrompt);
